import * as RadixDropdownMenu from '@radix-ui/react-dropdown-menu';
import { cn } from '@realtyfeed/utils';
import { Check } from 'react-feather';

export const DropdownMenu = RadixDropdownMenu.Root;
export const DropdownMenuTrigger = RadixDropdownMenu.Trigger;
export const DropdownMenuGroup = RadixDropdownMenu.Group;

type DropdownMenuContentProps = RadixDropdownMenu.DropdownMenuContentProps;

/**
 * The popover surface.
 *
 * Drawn on `ds-bg-surface` with a `ds-border-default` hairline, matching every
 * other floating surface in the system (the account switcher, the filter panel).
 * It used to be `bg-slate-50` on `border-slate-200` — stock Tailwind palette
 * greys, which the styling rules forbid for chrome and which read as a
 * lighter-grey card floating over the white ones behind it.
 */
export function DropdownMenuContent({
  className,
  sideOffset = 6,
  ...props
}: DropdownMenuContentProps) {
  return (
    <RadixDropdownMenu.Portal>
      <RadixDropdownMenu.Content
        sideOffset={sideOffset}
        className={cn(
          'z-50 min-w-[10rem] rounded-xl border border-ds-border-default bg-ds-bg-surface p-1.5 shadow-elevation-2 dark:border-slate-800 dark:bg-slate-900 dark:shadow-slate-950/40',
          className
        )}
        {...props}
      />
    </RadixDropdownMenu.Portal>
  );
}

type DropdownMenuItemProps = RadixDropdownMenu.DropdownMenuItemProps;

/**
 * A menu item.
 *
 * **The row is a flex line.** An item is an icon and a label, and Radix renders
 * whatever it is handed into a plain block — so `<Icon /> Label` stacked the
 * glyph *above* the text, which is what the navbar's Profile / Log out menu was
 * shipping. Every other call site had already worked around it by wrapping its
 * own children in `<span className="flex items-center gap-2">`; that belongs
 * here, once, so the next menu does not have to rediscover it. The wrappers
 * those call sites still have are harmless (a flex row inside a flex row).
 *
 * `disabled` items are styled from Radix's `data-disabled` attribute, not just
 * blocked: Radix already swallows `onSelect` for them, so without a visual
 * treatment a disabled item looked identical to an enabled one and reported as
 * "clicking Edit does nothing" rather than "Edit is unavailable for this row".
 * `pointer-events-none` also suppresses the hover highlight, which was the other
 * half of the illusion.
 */
export function DropdownMenuItem({ className, ...props }: DropdownMenuItemProps) {
  return (
    <RadixDropdownMenu.Item
      className={cn(
        'flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm text-ds-text-primary outline-none dark:text-slate-200',
        'data-[highlighted]:bg-ds-action-secondary-bg data-[highlighted]:text-ds-text-accent dark:data-[highlighted]:bg-slate-800 dark:data-[highlighted]:text-slate-50',
        'data-[disabled]:pointer-events-none data-[disabled]:cursor-not-allowed data-[disabled]:opacity-40',
        className
      )}
      {...props}
    />
  );
}

type DropdownMenuCheckboxItemProps = RadixDropdownMenu.DropdownMenuCheckboxItemProps;

/**
 * A menu item that carries a tick box — a filter menu's option, where
 * `DropdownMenuItem` is a command.
 *
 * It is `DropdownMenuItem`'s row with a box drawn in front of it, and the box is
 * this component's own markup rather than the caller's: the point of the wrapper
 * is that every multi-select menu in both portals draws the same one, and a
 * caller passing its own `<span>` was how the first two drifted. Radix supplies
 * `role="menuitemcheckbox"`, `aria-checked` and the `checked`/`onCheckedChange`
 * pair, which is the half a hand-rolled tick box always leaves out.
 *
 * **It does not close the menu.** Ticking boxes is one task and Radix's default
 * would close after each one, so `onSelect` is prevented here rather than in
 * every caller. A caller that genuinely wants a closing checkbox passes its own
 * `onSelect`, which runs after and can choose not to prevent.
 */
export function DropdownMenuCheckboxItem({
  className,
  children,
  onSelect,
  ...props
}: DropdownMenuCheckboxItemProps) {
  return (
    <RadixDropdownMenu.CheckboxItem
      onSelect={(event) => {
        event.preventDefault();
        onSelect?.(event);
      }}
      className={cn(
        'flex cursor-pointer items-center gap-2.5 rounded-md px-3 py-2 text-sm text-ds-text-primary outline-none dark:text-slate-200',
        'data-[highlighted]:bg-ds-action-secondary-bg data-[highlighted]:text-ds-text-accent dark:data-[highlighted]:bg-slate-800 dark:data-[highlighted]:text-slate-50',
        'data-[disabled]:pointer-events-none data-[disabled]:cursor-not-allowed data-[disabled]:opacity-40',
        className
      )}
      {...props}
    >
      <span
        aria-hidden
        className={cn(
          'flex size-[18px] shrink-0 items-center justify-center rounded-[3px] border border-ds-border-strong bg-ds-bg-surface dark:border-slate-700 dark:bg-slate-950/40',
          props.checked && 'border-ds-action-primary bg-ds-action-primary text-white'
        )}
      >
        <RadixDropdownMenu.ItemIndicator>
          <Check size={13} strokeWidth={3} aria-hidden />
        </RadixDropdownMenu.ItemIndicator>
      </span>
      {children}
    </RadixDropdownMenu.CheckboxItem>
  );
}

type DropdownMenuSeparatorProps = RadixDropdownMenu.DropdownMenuSeparatorProps;

/** The hairline between two groups of items. Decorative — Radix marks it so. */
export function DropdownMenuSeparator({ className, ...props }: DropdownMenuSeparatorProps) {
  return (
    <RadixDropdownMenu.Separator
      className={cn('-mx-1.5 my-1.5 h-px bg-ds-border-default dark:bg-slate-800', className)}
      {...props}
    />
  );
}
